import { RootSetting, FormFieldSettingType } from "./form-field-setting";
import { DynamicFormType, FormComponentsByType } from "../form-field/form-field-type";
import { SingleFormFieldSetting } from "../form-field/single/single-form-field-setting";
import { GroupFormFieldSetting } from "../form-field/group/group-form-field-setting";
import { ArrayFormFieldSetting } from "../form-field/array/array-form-field-setting";
import { FormFieldComponentType } from "../form-field/form-field-component";
import { SubType } from "./sub-type";
import { HiddenFormFieldSetting } from "../form-field/hidden/hidden-form-field-setting";

export type CombinedFormFieldSetting =
  | SingleFormFieldSetting
  | GroupFormFieldSetting
  | ArrayFormFieldSetting
  | HiddenFormFieldSetting;

export type FormFieldSettings = Map<string, CombinedFormFieldSetting>;

export const ROOT_ID = "__ROOT__";

export function createFormFieldSettings(
  rootSetting: RootSetting,
  settings: FormFieldSettings,
  defaultFormComponents: FormComponentsByType
): FormFieldSettings {
  if (settings.has(ROOT_ID)) {
    const illegalSetting = settings.get(ROOT_ID);
    throw new Error(`Illegal form field id ${illegalSetting.name} detected for ${illegalSetting.name}`);
  }

  const combinedSettings: FormFieldSettings = new Map();
  combinedSettings.set(ROOT_ID, {
    name: ROOT_ID,
    component: rootSetting.component,
    type: DynamicFormType.GROUP,
    metadata: rootSetting.metadata
  });

  settings.forEach((setting, id) =>
    combinedSettings.set(id, {
      ...setting,
      type: setting.type ? setting.type : (DynamicFormType.SINGLE as any)
    })
  );

  // add missing components from default form components
  for (let [, setting] of combinedSettings) {
    setting.type = SubType.toSubType(setting.type);
    if (setting.component || setting.type.type === DynamicFormType.HIDDEN) continue;
    setting.component = ensureDefaultFormComponents(defaultFormComponents, setting.name, setting.type);
  }

  return combinedSettings;
}

export function ensureDefaultFormComponents(
  defaultFormComponents: FormComponentsByType,
  name: string,
  _type: FormFieldSettingType
): FormFieldComponentType {
  if (!defaultFormComponents) {
    throw new Error(
      `No default form components available, but you used a DynamicFormType as an component identifier for ${name}. Did you add the module using DynamicFormsLiteModule.withDefaultComponents()?`
    );
  }

  const subType = SubType.toSubType(_type);
  const component = defaultFormComponents.get(subType.type)[subType.subType];

  if (!component) {
    throw new Error(`No default form component for type ${subType.subType} (${subType.type}) available.`);
  }

  return component;
}
